import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";

const PALETTE = [
  "#2f3891", "#0ea5e9", "#16a34a", "#d97706",
  "#dc2626", "#7c3aed", "#0891b2", "#db2777",
];

const colorFor = (name: string): string => {
  if (!name) return PALETTE[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return PALETTE[hash % PALETTE.length];
};

const initialOf = (name: string): string =>
  (name?.trim()?.[0] || "?").toUpperCase();

const buildSrc = (logo?: string | null, apiBase?: string): string | null => {
  if (!logo) return null;
  if (/^(https?:|data:|blob:)/i.test(logo)) return logo;
  const base = (apiBase || "").replace(/\/+$/, "");
  const path = String(logo).replace(/^\/+/, "");
  return base ? `${base}/${path}` : `/${path}`;
};

interface CompanyAvatarProps {
  name?: string | null;
  logo?: string | null;
  size?: number;
  rounded?: number | string;
  apiBase?: string;
  fontSize?: number;
  className?: string;
  imgFit?: "contain" | "cover";
  imgStyle?: React.CSSProperties;
}

const CompanyAvatar: React.FC<CompanyAvatarProps> = ({
  name,
  logo,
  size = 56,
  rounded = 2,
  apiBase,
  fontSize,
  className,
  imgFit = "contain",
  imgStyle,
}) => {
  const safeName = name || "";
  const src = buildSrc(logo, apiBase);
  const [errored, setErrored] = useState(false);
  useEffect(() => { setErrored(false); }, [src]);
  const showImg = !!src && !errored;
  const fs = fontSize ?? Math.round(size * 0.42);

  return (
    <Box
      className={className}
      sx={{
        width: size,
        height: size,
        borderRadius: rounded,
        bgcolor: showImg ? "#f8fafc" : colorFor(safeName),
        border: showImg ? "1px solid #e2e8f0" : "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {showImg ? (
        <img
          src={src!}
          alt={safeName}
          onError={() => setErrored(true)}
          style={{ width: "100%", height: "100%", objectFit: imgFit, ...imgStyle }}
        />
      ) : (
        <Typography
          sx={{
            color: "#fff",
            fontWeight: 800,
            fontSize: fs,
            lineHeight: 1,
            letterSpacing: 0.5,
          }}
        >
          {initialOf(safeName)}
        </Typography>
      )}
    </Box>
  );
};

export default CompanyAvatar;
